/**
 * Broker "My Loads" — map a bid row to one of the 8 tabs.
 * A row appears in "all" + exactly one stage tab.
 */
export type BrokerTabKey =
  | "all"
  | "bidding"
  | "awarded"
  | "dispatching"
  | "in_transit"
  | "delivered"
  | "closed"
  | "archive";

export interface BucketInput {
  bid_status: string; // pending | countered | accepted | withdrawn | rejected | lost
  is_awarded: boolean;
  lost_to_other: boolean;
  dispatch_status: string | null;
  dispatch_archived_at: string | null;
  /** Parent load status (expired | canceled | completed | ...), if readable. */
  load_status?: string | null;
  /** True when the parent load row is no longer readable/exists. */
  load_missing?: boolean;
}

/** Parent-load states that terminate the opportunity for a non-awarded bidder. */
const TERMINAL_LOAD_STATUSES = new Set(["canceled", "cancelled", "expired", "completed"]);

/** Returns the single non-"all" tab this row belongs to. */
export function bucketForRow(r: BucketInput): Exclude<BrokerTabKey, "all"> {
  // Archive supersedes everything (dispatch archived, bid withdrawn/rejected, or lost).
  if (r.dispatch_archived_at) return "archive";
  if (!r.is_awarded && (r.bid_status === "withdrawn" || r.bid_status === "rejected" || r.lost_to_other)) {
    return "archive";
  }

  // Parent opportunity is gone/closed and we did not win it — historical only.
  if (!r.is_awarded && (r.load_missing || TERMINAL_LOAD_STATUSES.has(r.load_status ?? ""))) {
    return "archive";
  }

  // Not awarded yet — still bidding.
  if (!r.is_awarded) return "bidding";

  // Awarded — bucket by dispatch status.
  const s = r.dispatch_status ?? "draft";
  switch (s) {
    case "draft":
    case "ready":
      return "awarded";
    case "assigned":
    case "en_route_to_pickup":
    case "at_pickup":
      return "dispatching";
    case "picked_up":
    case "en_route_to_delivery":
    case "at_delivery":
      return "in_transit";
    case "delivered":
      return "delivered";
    case "closed":
      return "closed";
    default:
      return "awarded";
  }
}

export const TAB_LABEL: Record<BrokerTabKey, string> = {
  all: "All",
  bidding: "Bidding",
  awarded: "Awarded",
  dispatching: "Dispatching",
  in_transit: "In Transit",
  delivered: "Delivered",
  closed: "Closed",
  archive: "Archive",
};

export const TAB_ORDER: BrokerTabKey[] = [
  "all", "bidding", "awarded", "dispatching", "in_transit", "delivered", "closed", "archive",
];
